import { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, addDoc, updateDoc, query, onSnapshot, where, Timestamp, getDocs, increment } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useClient } from '../contexts/ClientContext';
import { AlertEmailHistory } from '../types/alerts';

export const useAlertEmails = () => {
  const [emailHistory, setEmailHistory] = useState<AlertEmailHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { selectedClient } = useClient();

  // Cargar historial de emails en tiempo real
  useEffect(() => {
    let isMounted = true;

    if (!selectedClient) {
      setEmailHistory([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const emailsRef = collection(db, 'alertEmails');

    const emailsQuery = query(
      emailsRef,
      where('clientId', '==', selectedClient.clientId)
    );

    const unsubscribe = onSnapshot(emailsQuery, (snapshot) => {
      if (!isMounted) return;

      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as AlertEmailHistory[];

      // Ordenar en cliente para evitar índice compuesto
      data.sort((a, b) => {
        const aTime = (a.lastUsedAt as Timestamp | undefined)?.toMillis?.() || 0;
        const bTime = (b.lastUsedAt as Timestamp | undefined)?.toMillis?.() || 0;
        return bTime - aTime;
      });

      setEmailHistory(data);
      setLoading(false);
    }, (err) => {
      if (!isMounted) return;

      // Ignorar errores internos de Firestore que no afectan la funcionalidad
      if (err.message?.includes('INTERNAL ASSERTION FAILED')) {
        console.warn('Firestore internal warning (ignorable):', err.message);
        return;
      }

      console.error('Error loading email history:', err);
      setError('Error al cargar el historial de emails');
      setLoading(false);
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [selectedClient]);

  // Lista simple de emails (solo strings)
  const emails = useMemo(() => {
    return emailHistory.map(e => e.email);
  }, [emailHistory]);

  // Emails más usados (ordenados por uso)
  const mostUsedEmails = useMemo(() => {
    return [...emailHistory]
      .sort((a, b) => b.usageCount - a.usageCount)
      .map(e => e.email);
  }, [emailHistory]);

  // Registrar uso de un email (crear o actualizar)
  const trackEmailUsage = useCallback(async (email: string): Promise<void> => {
    if (!selectedClient) {
      throw new Error('No hay cliente seleccionado');
    }

    const normalizedEmail = email.toLowerCase().trim();

    if (!normalizedEmail || !normalizedEmail.includes('@')) {
      throw new Error('Email inválido');
    }

    try {
      // Buscar si ya existe
      const emailsRef = collection(db, 'alertEmails');
      const existingQuery = query(
        emailsRef,
        where('clientId', '==', selectedClient.clientId),
        where('email', '==', normalizedEmail)
      );

      const snapshot = await getDocs(existingQuery);

      if (snapshot.empty) {
        // Crear nuevo registro
        await addDoc(emailsRef, {
          clientId: selectedClient.clientId,
          email: normalizedEmail,
          lastUsedAt: Timestamp.now(),
          usageCount: 1
        });
      } else {
        // Actualizar existente
        const docRef = snapshot.docs[0].ref;
        await updateDoc(docRef, {
          lastUsedAt: Timestamp.now(),
          usageCount: increment(1)
        });
      }
    } catch (err) {
      console.error('Error tracking email usage:', err);
      throw new Error(`Error al registrar el email: ${err instanceof Error ? err.message : 'Error desconocido'}`);
    }
  }, [selectedClient]);

  // Registrar múltiples emails
  const trackMultipleEmails = useCallback(async (emailList: string[]): Promise<void> => {
    const uniqueEmails = Array.from(new Set(emailList.map(e => e.toLowerCase().trim())));

    for (const email of uniqueEmails) {
      try {
        await trackEmailUsage(email);
      } catch (err) {
        console.error(`Error tracking email ${email}:`, err);
        // Continuar con los demás
      }
    }
  }, [trackEmailUsage]);

  // Validar formato de email
  const isValidEmail = useCallback((email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  }, []);

  // Obtener emails sugeridos (últimos N usados)
  const getRecentEmails = useCallback((count: number = 5): string[] => {
    return emails.slice(0, count);
  }, [emails]);

  return {
    emailHistory,
    emails,
    mostUsedEmails,
    loading,
    error,
    trackEmailUsage,
    trackMultipleEmails,
    isValidEmail,
    getRecentEmails
  };
};
